import React, { useState, useRef, useEffect, useMemo } from 'react';

// Searchable, grouped account picker built to scale to many accounts (the plain
// <select> the old page used doesn't hold up past a handful). Groups Live vs
// Paper, filters on name + IBKR account number, and shows the total count.
export default function AccountCombobox({ accounts, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const list = useMemo(() => accounts || [], [accounts]);
  const current = useMemo(
    () => list.find((a) => a.id === value) || list.find((a) => a.is_default) || list[0] || null,
    [list, value],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((a) => `${a.name} ${a.account_id || ''}`.toLowerCase().includes(needle));
  }, [list, q]);

  const live = filtered.filter((a) => !a.is_paper);
  const paper = filtered.filter((a) => a.is_paper);

  const initial = (a) => (a?.is_paper ? 'P' : 'L');

  const Item = ({ a }) => (
    <button
      type="button"
      onClick={() => { onChange(a.id); setOpen(false); }}
      className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-[#1c2330] ${
        a.id === current?.id ? 'text-brand-gold' : 'text-gray-700 dark:text-gray-200'
      }`}
    >
      <span className="w-6 h-6 rounded bg-brand-gold/10 text-brand-gold grid place-items-center text-[11px] font-bold shrink-0">
        {initial(a)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium truncate">{a.name}{a.is_default ? ' · default' : ''}</span>
        <span className="block text-[11px] text-gray-400 truncate">{a.account_id || '—'}</span>
      </span>
      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
        a.is_paper ? 'bg-blue-500/10 text-blue-400' : 'bg-red-500/10 text-red-400'
      }`}>{a.is_paper ? 'paper' : 'live'}</span>
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 min-w-[300px] px-3.5 py-1.5 rounded-lg bg-white dark:bg-[#161b22] border border-gray-300 dark:border-gray-700 hover:border-brand-gold transition-colors"
      >
        <span className="w-6 h-6 rounded-md bg-brand-gold/10 text-brand-gold grid place-items-center text-[11px] font-bold shrink-0">
          {initial(current)}
        </span>
        <span className="min-w-0 flex-1 text-left flex items-baseline gap-2">
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{current?.name || 'No account'}</span>
          <span className="text-[11px] text-gray-400 truncate shrink-0">{current?.account_id || '—'}</span>
        </span>
        <span className="text-xs text-gray-400">▾</span>
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+6px)] w-[360px] z-40 rounded-xl bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden">
          <input
            ref={searchRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search accounts…"
            className="w-full px-3.5 py-3 text-sm bg-gray-50 dark:bg-[#0d1117] border-b border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none"
          />
          <div className="max-h-[380px] overflow-y-auto scrollbar-thin">
            {live.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-widest text-gray-400 font-semibold">Live</div>
                {live.map((a) => <Item key={a.id} a={a} />)}
              </>
            )}
            {paper.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-widest text-gray-400 font-semibold">Paper</div>
                {paper.map((a) => <Item key={a.id} a={a} />)}
              </>
            )}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-gray-400 italic">No accounts match “{q}”.</div>
            )}
          </div>
          <div className="px-3 py-2 text-[10px] text-gray-400 border-t border-gray-200 dark:border-gray-800">
            {list.length} account{list.length === 1 ? '' : 's'}
          </div>
        </div>
      )}
    </div>
  );
}
